import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    TrashIcon,
    DocumentArrowUpIcon
} from '@heroicons/react/24/outline';

interface Skill {
    _id: string;
    name: string;
    description?: string;
    isActive: boolean;
    isPopular: boolean;
    usageCount: number;
    createdAt: string;
    updatedAt: string;
    createdBy: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
    };
}

interface SkillFormData {
    name: string;
    description: string;
    isPopular: boolean;
    isActive: boolean;
}

const Skills: React.FC = () => {
    const dispatch = useAppDispatch();
    const [skills, setSkills] = useState<Skill[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkSkills, setBulkSkills] = useState('');

    // Filters and search
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortBy, setSortBy] = useState('usageCount');
    const [sortOrder, setSortOrder] = useState('desc');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalSkills, setTotalSkills] = useState(0);

    // Form data
    const [formData, setFormData] = useState<SkillFormData>({
        name: '',
        description: '',
        isPopular: false,
        isActive: true
    });


    const fetchSkills = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: '10'
            });

            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter) params.append('isActive', statusFilter);
            if (sortBy) params.append('sortBy', sortBy);
            if (sortOrder) params.append('sortOrder', sortOrder);

            const response = await api.get(`/skills?${params}`);
            setSkills(response.data.data);
            setTotalPages(response.data.pagination.pages);
            setTotalSkills(response.data.pagination.total);
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch skills',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch, currentPage, searchTerm, statusFilter, sortBy, sortOrder]);

    useEffect(() => {
        fetchSkills();
    }, [fetchSkills]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingSkill) {
                await api.put(`/skills/${editingSkill._id}`, formData);
                dispatch(showToast({ message: 'Skill updated successfully', type: 'success' }));
            } else {
                await api.post('/skills', formData);
                dispatch(showToast({ message: 'Skill created successfully', type: 'success' }));
            }
            setShowModal(false);
            setEditingSkill(null);
            resetForm();
            fetchSkills();
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to save skill',
                type: 'error'
            }));
        }
    };

    const handleDelete = async (skillId: string) => {
        if (!window.confirm('Are you sure you want to delete this skill? This action cannot be undone.')) return;

        try {
            await api.delete(`/skills/${skillId}`);
            dispatch(showToast({ message: 'Skill deleted successfully', type: 'success' }));
            fetchSkills();
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Failed to delete skill';

            // If skill is being used, offer deactivation instead
            if (errorMessage.includes('being used')) {
                const shouldDeactivate = window.confirm(
                    `${errorMessage}\n\nWould you like to deactivate this skill instead?`
                );
                if (shouldDeactivate) {
                    await handleDeactivate(skillId);
                }
            } else {
                dispatch(showToast({
                    message: errorMessage,
                    type: 'error'
                }));
            }
        }
    };

    const handleDeactivate = async (skillId: string) => {
        try {
            await api.put(`/skills/${skillId}/deactivate`);
            dispatch(showToast({ message: 'Skill deactivated successfully', type: 'success' }));
            fetchSkills();
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to deactivate skill',
                type: 'error'
            }));
        }
    };

    const handleBulkCreate = async () => {
        try {
            const skillsArray = bulkSkills
                .split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0)
                .map(name => ({ name, category: 'other' }));

            await api.post('/skills/bulk', { skills: skillsArray });
            dispatch(showToast({ message: 'Skills created successfully', type: 'success' }));
            setShowBulkModal(false);
            setBulkSkills('');
            fetchSkills();
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to create skills',
                type: 'error'
            }));
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            isPopular: false,
            isActive: true
        });
    };

    const openEditModal = (skill: Skill) => {
        setEditingSkill(skill);
        setFormData({
            name: skill.name,
            description: skill.description || '',
            isPopular: skill.isPopular,
            isActive: skill.isActive
        });
        setShowModal(true);
    };

    const openCreateModal = () => {
        setEditingSkill(null);
        resetForm();
        setShowModal(true);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };


    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Skills Management</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                        Manage platform skills and categories
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex space-x-3">
                    <button
                        onClick={() => setShowBulkModal(true)}
                        className="btn-secondary flex items-center"
                    >
                        <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                        Bulk Create
                    </button>
                    <button
                        onClick={openCreateModal}
                        className="btn-primary flex items-center"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Add Skill
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Search */}
                    <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search skills..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input-field pl-10"
                        />
                    </div>


                    {/* Status Filter */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="input-field"
                    >
                        <option value="">All Status</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>

                    {/* Sort */}
                    <select
                        value={`${sortBy}-${sortOrder}`}
                        onChange={(e) => {
                            const [field, order] = e.target.value.split('-');
                            setSortBy(field);
                            setSortOrder(order);
                        }}
                        className="input-field"
                    >
                        <option value="usageCount-desc">Most Used</option>
                        <option value="usageCount-asc">Least Used</option>
                        <option value="name-asc">Name A-Z</option>
                        <option value="name-desc">Name Z-A</option>
                        <option value="createdAt-desc">Newest</option>
                        <option value="createdAt-asc">Oldest</option>
                    </select>
                </div>
            </div>

            {/* Skills Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Skill
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Usage
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {skills.map((skill) => (
                                        <tr key={skill._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {skill.name}
                                                    </div>
                                                    {skill.description && (
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                            {skill.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {skill.usageCount}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-2">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${skill.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {skill.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                    {skill.isPopular && (
                                                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                            Popular
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                {formatDate(skill.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => openEditModal(skill)}
                                                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(skill._id)}
                                                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                                <div className="flex-1 flex justify-between sm:hidden">
                                    <button
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                        disabled={currentPage === totalPages}
                                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700 dark:text-gray-300">
                                            Showing <span className="font-medium">{((currentPage - 1) * 10) + 1}</span> to{' '}
                                            <span className="font-medium">{Math.min(currentPage * 10, totalSkills)}</span> of{' '}
                                            <span className="font-medium">{totalSkills}</span> results
                                        </p>
                                    </div>
                                    <div>
                                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                            <button
                                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                                disabled={currentPage === 1}
                                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Previous
                                            </button>
                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                                <button
                                                    key={page}
                                                    onClick={() => setCurrentPage(page)}
                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage
                                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            ))}
                                            <button
                                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                                disabled={currentPage === totalPages}
                                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Next
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                {editingSkill ? 'Edit Skill' : 'Create New Skill'}
                            </h3>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Skill Name
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="input-field mt-1"
                                        placeholder="Enter skill name"
                                    />
                                </div>


                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="input-field mt-1"
                                        rows={3}
                                        placeholder="Enter skill description (optional)"
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                        Active skill
                                    </label>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="isPopular"
                                        checked={formData.isPopular}
                                        onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="isPopular" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                        Mark as popular skill
                                    </label>
                                </div>

                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                        className="btn-secondary"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                    >
                                        {editingSkill ? 'Update' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Create Modal */}
            {showBulkModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                Bulk Create Skills
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Skills (one per line)
                                    </label>
                                    <textarea
                                        value={bulkSkills}
                                        onChange={(e) => setBulkSkills(e.target.value)}
                                        className="input-field mt-1"
                                        rows={8}
                                        placeholder="JavaScript&#10;React&#10;Node.js&#10;Python&#10;..."
                                    />
                                </div>

                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowBulkModal(false)}
                                        className="btn-secondary"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleBulkCreate}
                                        className="btn-primary"
                                    >
                                        Create Skills
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Skills;
